/**
 * Preview-generation engine, with two clearly separated modes controlled by
 * a single switch: window.PfGenerationConfig.MODE (see generation-config.js).
 *
 * 1) 'demo' mode: simulates generationProgress/generationStatus locally with
 *    a natural (non-linear) pace — fast at first, occasional pauses, slower
 *    near 80-95%, then completes. No network calls.
 *
 * 2) 'real' mode: talks to our own backend (PRENDFORM_API_BASE), which is
 *    the only thing that knows the real Tripo API key. This file NEVER
 *    calls Tripo directly and NEVER holds any secret — only the public
 *    backend base URL from generation-config.js.
 *
 *    Flow (master-first — no style chosen yet at this point): POST
 *    /api/tripo/generate as multipart/form-data (only the original photo
 *    file and petName — NO style; the backend always generates the
 *    realistic master first) -> poll GET /api/tripo/status?task_id=... for
 *    master_status (the realistic master; figure_status is an older,
 *    non-blocking fallback name for the same thing). Once it succeeds, the backend
 *    generates the animated_3d style variant (christmas/halloween were
 *    removed from the product) in the background under the same task;
 *    polling continues, and 'complete' (navigation to style-picker.js) is
 *    held back until both are ready (or a generous timeout elapses) so
 *    /pages/elegir-estilo can show its 2 cards fully loaded instead of
 *    skeletons popping in one by one. Multiview (front/side) is a SEPARATE,
 *    later step: it only starts once the customer picks a style there —
 *    see style-picker.js's Continuer handler, which calls
 *    /api/tripo/status with a selected_style query param.
 *
 * Public API (used by generation-page.js): on(), startGeneration(),
 * updateGenerationProgress(), handleGenerationComplete(), handleGenerationError(),
 * retryGeneration(). Consumers subscribe via on('progress'|'complete'|'error', cb).
 */
window.PfGeneration = (() => {
  const config = window.PfGenerationConfig || { API_BASE: '', MODE: 'demo', DEBUG: false };
  const API_BASE = config.API_BASE;

  const DEMO_MESSAGES = [
    'Analyse de la photo…',
    'Détection des traits de {name}…',
    'Préparation du modèle…',
    'Mise en forme de {name}…',
    'Ajout des derniers détails…',
    'Préparation de ton aperçu…',
  ];

  const POLL_INTERVAL_MS = 2500;
  // Only for the master-wait phase (masterStatus !== 'success' below):
  // polling more often just lets us NOTICE master_status: 'success' a
  // little sooner (up to ~1.5s sooner than the 2.5s cadence) - this alone
  // is what starts animated_3d's Kie.ai task a bit earlier, since that
  // task is only created once we've seen master succeed. Same read-only
  // GET /api/tripo/status call, no new Kie.ai task, no quality/model
  // change. Deliberately NOT applied to the animated_3d catch-up poll
  // further down (still POLL_INTERVAL_MS there) - that phase isn't part
  // of what's being tightened here.
  const MASTER_POLL_INTERVAL_MS = 1000;
  const POLL_RETRY_BACKOFF_MS = [2000, 3000, 5000];

  // Friendly messages for the new /api/tripo/generate error codes (Fase
  // 1B). Anything not listed here falls back to the existing generic
  // message — no other copy in this file changes.
  const GENERATE_ERROR_MESSAGES = {
    generate_conflict: 'Une génération est déjà en cours pour cette photo. Réessaie dans un instant.',
    generate_failed_retryable: 'La génération a échoué au démarrage. Réessaie.',
    generation_persist_failed: "Un problème est survenu pendant l'enregistrement de ta personnalisation. Réessaie dans un instant.",
  };

  const listeners = { progress: [], complete: [], error: [] };
  let demoTimers = [];
  let pollTimer = null;
  let pollFailureCount = 0;
  let generationInFlight = false;
  let petName = 'ta mascotte';
  let currentJobId = null;

  function log(...args) {
    if (config.DEBUG) console.info('[PRENDFORM]', ...args);
  }

  /**
   * Plain fetch() never times out on its own — a hung backend function or a
   * stalled connection leaves the promise pending forever, which looks
   * identical to "stuck at 0%, no error, no console message" from the
   * customer's side. This wraps fetch() with an AbortController so a hang
   * becomes a real, visible, retriable error instead of an infinite wait.
   */
  async function fetchWithTimeout(url, options, timeoutMs) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(url, Object.assign({}, options, { signal: controller.signal }));
    } catch (err) {
      if (err && err.name === 'AbortError') {
        throw { code: 'timeout', message: `Aucune réponse du serveur après ${timeoutMs}ms.` };
      }
      throw err;
    } finally {
      clearTimeout(timer);
    }
  }

  /** Reads the response body as text first so a non-JSON or empty body
   * (e.g. a hung function that eventually 502s) never throws while we're
   * trying to log/inspect it — always returns a printable string. */
  async function readBodySafe(res) {
    try {
      return await res.text();
    } catch (e) {
      return '(impossible de lire le corps de la réponse)';
    }
  }

  function on(event, callback) {
    if (listeners[event]) listeners[event].push(callback);
  }

  function emit(event, payload) {
    (listeners[event] || []).forEach((cb) => cb(payload));
  }

  // --- Public state helpers -------------------------------------------
  function updateGenerationProgress(progress, message) {
    emit('progress', { progress, message, jobId: currentJobId });
  }

  function handleGenerationComplete(result) {
    generationInFlight = false;
    persistGeneratedModel(result);
    emit('complete', result);
  }

  function handleGenerationError(error) {
    generationInFlight = false;
    emit('error', error);
  }

  /**
   * TODO(backend): model_url and rendered_image_url returned by Tripo are
   * TEMPORARY (per Tripo's own docs, roughly ~5 minutes before they expire).
   * This is the single place where — once we have permanent storage for
   * generated assets — we should download/copy the GLB (and the rendered
   * image) into our own storage right after a successful generation, before
   * the temporary URL expires. Not implemented yet on purpose.
   */
  function persistGeneratedModel(result) {
    log('persistGeneratedModel() stub — model_url from Tripo is temporary (~5 min), not yet copied to permanent storage.');
  }

  // --- Mode dispatch -----------------------------------------------------
  function startGeneration(options) {
    options = options || {};
    petName = options.petName || 'ta mascotte';

    if (config.MODE === 'demo') {
      clearDemoTimers();
      currentJobId = 'demo-' + Date.now();
      runDemoGeneration();
      return;
    }

    startRealGeneration(options);
  }

  function retryGeneration(options) {
    // Clear only the generation-related fields — never the rest of the
    // configurator's answers (email, petName, photo, notes, game result…).
    // animated3dTaskId included: a fresh generation gets a brand new
    // master_task_id, so any previously-known animated_3d_task_id no
    // longer applies and must never be echoed back against it.
    window.PfFunnel?.set({
      generationJobId: undefined,
      generationStatus: undefined,
      generationProgress: undefined,
      generationResult: undefined,
      generationError: undefined,
      animated3dTaskId: undefined,
    });
    stopPolling();
    generationInFlight = false;
    currentJobId = null;
    startGeneration(options);
  }

  // --- DEMO mode (unchanged, simulated) -----------------------------------
  function clearDemoTimers() {
    demoTimers.forEach((id) => clearTimeout(id));
    demoTimers = [];
  }

  function demoMessageForProgress(progress) {
    const index = Math.min(
      DEMO_MESSAGES.length - 1,
      Math.floor((progress / 100) * DEMO_MESSAGES.length)
    );
    return DEMO_MESSAGES[index].replace('{name}', petName);
  }

  function runDemoGeneration() {
    let progress = 0;

    const tick = () => {
      let increment;
      if (progress < 40) {
        increment = 4 + Math.random() * 7;
      } else if (progress < 80) {
        increment = 2 + Math.random() * 4;
      } else {
        increment = 0.5 + Math.random() * 2;
      }

      progress = Math.min(100, progress + increment);
      updateGenerationProgress(progress, demoMessageForProgress(progress));

      if (progress >= 100) {
        demoTimers.push(
          setTimeout(() => {
            handleGenerationComplete({ demo: true, jobId: currentJobId });
          }, 500)
        );
        return;
      }

      let delay = 350 + Math.random() * 550;
      if (Math.random() < 0.12) delay += 900 + Math.random() * 600;

      demoTimers.push(setTimeout(tick, delay));
    };

    demoTimers.push(setTimeout(tick, 400));
  }

  // --- REAL mode: single direct-upload generate call -> poll -------------
  const ACCEPTED_PHOTO_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  function realMessageForProgress(progress) {
    if (progress <= 10) return 'Analyse de la photo…';
    if (progress <= 30) return `Détection des traits de ${petName}…`;
    if (progress <= 55) return `Mise en forme de ${petName}…`;
    if (progress <= 75) return 'Travail sur les finitions…';
    if (progress <= 94) return 'Préparation du modèle…';
    return 'Ajout des derniers détails…';
  }

  // --- Pre-send photo optimization ----------------------------------------
  // Vercel rejects requests over ~4.5MB with a 413 Content Too Large BEFORE
  // our backend code ever runs. pet-photo.js already compresses large
  // photos at upload time, but only down to its own (looser) threshold —
  // this is a second, independent pass right before the actual upload,
  // guaranteeing the file that leaves the browser always stays safely
  // under that limit regardless of what the upload step already did.
  const MAX_UPLOAD_BYTES = 3.5 * 1024 * 1024;
  const MAX_UPLOAD_DIMENSION = 2048;
  const UPLOAD_QUALITY_STEPS = [0.88, 0.84, 0.8, 0.76];

  async function loadImageBitmapSafe(file) {
    if ('createImageBitmap' in window) {
      try {
        return await createImageBitmap(file, { imageOrientation: 'from-image' });
      } catch (e) {
        // Fall through to the <img> fallback below.
      }
    }
    const objectUrl = URL.createObjectURL(file);
    try {
      return await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('image_load_failed'));
        img.src = objectUrl;
      });
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  function drawCappedCanvas(source, maxDimension) {
    const width = source.width || source.naturalWidth;
    const height = source.height || source.naturalHeight;
    // Never upscale — only shrink photos actually larger than the target,
    // keeping the original aspect ratio and orientation exactly.
    const scale = Math.min(1, maxDimension / Math.max(width, height));
    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    canvas.getContext('2d').drawImage(source, 0, 0, targetWidth, targetHeight);
    return canvas;
  }

  function canvasToJpegBlob(canvas, quality) {
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  }

  /**
   * Guarantees the File handed to FormData stays safely under Vercel's
   * ~4.5MB request-size limit. Resizes to at most MAX_UPLOAD_DIMENSION px
   * on the longest side (no-op if already smaller — never upscales), then
   * re-encodes as JPEG starting at quality 0.88 and only drops quality
   * further (0.84 → 0.80 → 0.76) if still over MAX_UPLOAD_BYTES — an
   * already small/adequate photo is typically re-encoded once, at the
   * highest quality, and never touched more aggressively than needed.
   */
  async function optimizePhotoForUpload(file) {
    const originalMb = (file.size / (1024 * 1024)).toFixed(2);
    const source = await loadImageBitmapSafe(file);
    const originalWidth = source.width || source.naturalWidth;
    const originalHeight = source.height || source.naturalHeight;
    console.info(`[PRENDFORM] Original image: ${originalMb} MB, ${originalWidth}x${originalHeight}`);

    const canvas = drawCappedCanvas(source, MAX_UPLOAD_DIMENSION);
    if (typeof source.close === 'function') source.close();

    let blob = null;
    for (const quality of UPLOAD_QUALITY_STEPS) {
      blob = await canvasToJpegBlob(canvas, quality);
      if (blob && blob.size <= MAX_UPLOAD_BYTES) break;
    }
    if (!blob) {
      throw { code: 'photo_optimize_failed', message: "La photo n'a pas pu être optimisée." };
    }

    const optimizedMb = (blob.size / (1024 * 1024)).toFixed(2);
    console.info(`[PRENDFORM] Optimized image: ${optimizedMb} MB, ${canvas.width}x${canvas.height}`);

    const newName = file.name.replace(/\.[^./\\]+$/, '') + '.jpg';
    return new File([blob], newName, { type: 'image/jpeg' });
  }

  // --- Idempotency-Key (Fase 1B) ------------------------------------------
  // One key per LOGICAL generation attempt (never derived from the photo
  // itself — see requestGeneration below): created the first time a given
  // photo is sent, reused as-is on every technical retry of that same
  // attempt (network hiccup, 409, 502, refresh), and only rotated when the
  // customer deliberately starts a new personalization. Backend charset:
  // [A-Za-z0-9_.:-], 1-200 chars — a UUID always satisfies this.
  function makeIdempotencyKey() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'idem-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
  }

  // Identity of "which personalization is this" must be EXPLICIT, not
  // inferred from the photo — a customer can deliberately start a new
  // personalization with the exact same photo (same bytes, same name), and
  // that must still get a new key/design_id. activeFigureIndex is the
  // existing, explicit signal already written by the "different pet" flow
  // (finalize-figurine.js / checkout-figurine.js: { activeFigureIndex:
  // figures.length }) the moment the customer deliberately starts figure
  // N+1 — it advances ONLY there and nowhere else, and stays untouched
  // across refresh/retry, which makes it the correct attempt boundary.
  function currentFigureIndex(state) {
    return state.activeFigureIndex || 0;
  }

  // Secondary, non-authoritative signal: still useful to catch a photo that
  // changed WITHOUT going through the official "different pet" reset (e.g.
  // the customer used the browser's back button and picked another file
  // for the same figure) — but never the sole authority for identity, and
  // never sent to the backend or used as the Idempotency-Key value itself.
  function photoFingerprint(file) {
    return file ? `${file.name}|${file.size}|${file.type}` : null;
  }

  // True when the stored idempotencyKey/designId belong to a figure the
  // customer has since moved past (activeFigureIndex advanced) — i.e. a
  // deliberate new personalization started and this identity is stale.
  function isStaleAttempt(state) {
    return state.idempotencyKey !== undefined && state.idempotencyAttemptIndex !== currentFigureIndex(state);
  }

  // Drops a superseded personalization's identity (idempotencyKey,
  // fingerprint, designId, masterTaskId) so it can never leak into the new
  // one. Safe to call unconditionally — a no-op when nothing is stale.
  // Returns the current PfFunnel state either way. animated3dTaskId is
  // tied to a specific masterTaskId (the backend only ever resolves it
  // against whatever master_task_id was sent alongside it) — it must be
  // cleared in lockstep with masterTaskId, or a stale id from the PREVIOUS
  // pet's generation could get echoed back against a brand new master
  // task and resolve to the wrong animal's task.
  function clearStaleAttempt() {
    const state = window.PfFunnel ? window.PfFunnel.get() : {};
    if (!isStaleAttempt(state)) return state;
    log('New personalization detected (activeFigureIndex advanced) — clearing the previous design identity.');
    return window.PfFunnel.set({
      idempotencyKey: undefined,
      idempotencyPhotoFingerprint: undefined,
      idempotencyAttemptIndex: undefined,
      designId: undefined,
      masterTaskId: undefined,
      animated3dTaskId: undefined,
    });
  }

  function getIdempotencyKeyForPhoto(photoFile) {
    const state = window.PfFunnel ? window.PfFunnel.get() : {};
    const figureIndex = currentFigureIndex(state);
    const fingerprint = photoFingerprint(photoFile);

    const sameAttempt =
      !!state.idempotencyKey &&
      state.idempotencyAttemptIndex === figureIndex &&
      state.idempotencyPhotoFingerprint === fingerprint;

    if (sameAttempt) return state.idempotencyKey;

    // No key yet, OR a new personalization started (activeFigureIndex
    // advanced — the explicit signal), OR the photo changed unexpectedly
    // within the same personalization (secondary safety net). Either way,
    // the previous design/task identity no longer applies.
    const key = makeIdempotencyKey();
    window.PfFunnel?.set({
      idempotencyKey: key,
      idempotencyPhotoFingerprint: fingerprint,
      idempotencyAttemptIndex: figureIndex,
      designId: undefined,
      masterTaskId: undefined,
    });
    log('New Idempotency-Key for this personalization:', key);
    return key;
  }

  async function postGenerate(formData, idempotencyKey) {
    const url = `${API_BASE}/api/tripo/generate`;
    const res = await fetchWithTimeout(
      url,
      { method: 'POST', body: formData, headers: { 'Idempotency-Key': idempotencyKey } },
      45000
    );
    const bodyText = await readBodySafe(res);
    let data = null;
    try {
      data = bodyText ? JSON.parse(bodyText) : null;
    } catch (e) {
      data = null;
    }
    return { status: res.status, ok: res.ok, data, bodyText };
  }

  // Automatic retries here are intentionally narrow: only a 409 (this
  // Idempotency-Key already has a request in flight — a transient state
  // that normally resolves within seconds) is retried on its own, always
  // with the SAME key. Every other failure (502 with or without
  // master_task_id, or any other error) surfaces as a controlled error and
  // is retried only manually (the existing "Réessayer" button), which also
  // always reuses the same key since the photo hasn't changed.
  const CONFLICT_RETRY_DELAYS_MS = [1500, 2500];

  /**
   * Single call: the (optimized) photo file goes straight to
   * POST /api/tripo/generate as multipart/form-data — no more presign/PUT
   * round trip. The file is sent as a real File/Blob inside FormData, never
   * as JSON or a plain string, and the ORIGINAL file is never sent —
   * only the optimized one built above.
   *
   * IMPORTANT: never set a Content-Type header on a FormData request. The
   * browser computes the multipart boundary itself and needs to write the
   * full "multipart/form-data; boundary=..." header on its own — setting
   * it manually (or setting any Content-Type at all here) produces exactly
   * the "Invalid multipart/form-data body" error this fixes.
   */
  async function requestGeneration(photoFile, petNameValue) {
    const optimizedFile = await optimizePhotoForUpload(photoFile);
    const idempotencyKey = getIdempotencyKeyForPhoto(photoFile);
    const petNotesValue = (window.PfFunnel && window.PfFunnel.get().petNotes) || '';

    const buildFormData = () => {
      // NO style field here on purpose: the backend always generates the
      // realistic master first, regardless of style. Style is only chosen
      // afterward, on /pages/elegir-estilo, once the 4 variants are visible.
      const formData = new FormData();
      formData.append('file', optimizedFile, optimizedFile.name);
      // petName (camelCase) is the field the existing prompt/master
      // generation already relies on — kept untouched. pet_name/pet_notes
      // (snake_case) are the Fase 1B fields the backend persists alongside
      // the design; sent in addition to, never instead of, petName.
      formData.append('petName', petNameValue);
      formData.append('pet_name', petNameValue);
      if (petNotesValue) formData.append('pet_notes', petNotesValue);
      return formData;
    };

    log('Building FormData for /api/tripo/generate (no style — master generation):', {
      file: { name: optimizedFile.name, type: optimizedFile.type, size: optimizedFile.size },
      petName: petNameValue,
      pet_notes: petNotesValue,
      idempotencyKey,
    });

    let attempt = 0;
    for (;;) {
      const response = await postGenerate(buildFormData(), idempotencyKey);
      log('generate response status:', response.status, response.data || response.bodyText);

      if (response.ok) {
        const data = response.data || {};
        // master_task_id is the CURRENT backend contract. figure_task_id/
        // task_id/preview_task_id are older shapes, accepted only as
        // non-blocking legacy fallbacks — never required.
        const taskId = data.master_task_id || data.figure_task_id || data.task_id || data.preview_task_id;
        if (!taskId) {
          console.error('[PRENDFORM] generate response missing master_task_id:', data);
          throw { code: 'generate_invalid_response', body: data };
        }
        window.PfFunnel?.set({ designId: data.design_id || null });
        log('master_task_id received:', taskId, 'design_id:', data.design_id);
        return { task_id: taskId, design_id: data.design_id || null };
      }

      const errDesignId = response.data && response.data.design_id;
      const errMasterTaskId = response.data && response.data.master_task_id;
      if (errDesignId) window.PfFunnel?.set({ designId: errDesignId });

      if (response.status === 409) {
        attempt += 1;
        if (attempt <= CONFLICT_RETRY_DELAYS_MS.length) {
          const delay = CONFLICT_RETRY_DELAYS_MS[attempt - 1];
          log(`409 (generación ya en curso con esta Idempotency-Key) — reintentando en ${delay}ms con la MISMA key.`);
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }
        throw { code: 'generate_conflict', status: 409, designId: errDesignId };
      }

      if (response.status === 502 && errMasterTaskId) {
        // generation_persist_failed: never auto-retry, never rotate the
        // key — just a controlled error, preserving both ids.
        window.PfFunnel?.set({ masterTaskId: errMasterTaskId });
        throw {
          code: 'generation_persist_failed',
          status: 502,
          designId: errDesignId,
          masterTaskId: errMasterTaskId,
        };
      }

      if (response.status === 502) {
        throw { code: 'generate_failed_retryable', status: 502, designId: errDesignId };
      }

      console.error('[PRENDFORM] generate failed:', response.status, response.bodyText);
      throw { code: 'generate_failed', status: response.status, body: response.bodyText };
    }
  }

  // --- Design recovery (Fase 1B) -------------------------------------------
  // GET /api/tripo/design?design_id=... lets a browser that still holds a
  // valid design_id (e.g. after a refresh, or sessionStorage partially
  // missing a field) recover its basic data from the backend. This does
  // NOT rebuild the whole funnel step-by-step — it only restores the
  // identity/basic fields into PfFunnel state so nothing is lost.
  async function recoverDesign(designId) {
    if (!designId) return null;
    try {
      const url = `${API_BASE}/api/tripo/design?design_id=${encodeURIComponent(designId)}`;
      const res = await fetchWithTimeout(url, {}, 10000);
      if (!res.ok) {
        log('recoverDesign: GET /api/tripo/design failed with status', res.status);
        return null;
      }
      const bodyText = await readBodySafe(res);
      let data;
      try {
        data = JSON.parse(bodyText);
      } catch (e) {
        log('recoverDesign: response is not valid JSON:', bodyText);
        return null;
      }

      const patch = { designId };
      if (data.pet_name) patch.petName = data.pet_name;
      if (data.pet_notes) patch.petNotes = data.pet_notes;
      if (data.master_task_id) patch.masterTaskId = data.master_task_id;
      if (data.selected_style) patch.selectedStyle = data.selected_style;
      if (data.base_option) patch.baseOption = data.base_option;
      if (data.original_asset) patch.originalAsset = data.original_asset;
      if (data.status) patch.designStatus = data.status;

      // Fase 3B: designs.status is synced to 'completed' server-side
      // (status.ts's syncDesignStatusBestEffort) only once Kie.ai has
      // actually finished - safe to mirror into generationStatus so a
      // browser with incomplete/lost local state (different device, or a
      // partial session) doesn't fall through to starting a brand new
      // (paid) generation for a design that's already done. Never set to
      // anything else speculatively - only this one confirmed value.
      if (data.status === 'completed') {
        patch.generationStatus = 'completed';
      }

      // Fase 3B: merge backend-persisted previews (survive Kie.ai's own
      // ~24h temporary URL expiry, and a browser that lost its local
      // state entirely) into the SAME generationResult fields every page
      // already reads (realisticUrl/animated3dUrl) - this is what lets
      // style-picker.js/finalize-figurine.js/base-picker.js/
      // checkout-figurine.js pick these up with ZERO changes of their
      // own. Only ever FILLS IN a missing value, never overwrites an
      // already-working URL from this session's own live generation -
      // and never invents a URL for an asset the backend didn't confirm
      // exists (preview_realistic_asset/preview_selected_style_asset
      // null → nothing added, exactly as before). Uses the new read-only
      // GET /api/tripo/asset proxy - never the private r2_key itself.
      const existingResult = (window.PfFunnel && window.PfFunnel.get().generationResult) || {};
      const mergedResult = Object.assign({}, existingResult);
      let resultChanged = false;
      if (data.preview_realistic_asset && !mergedResult.realisticUrl) {
        mergedResult.realisticUrl = `${API_BASE}/api/tripo/asset?design_id=${encodeURIComponent(designId)}&kind=preview_realistic`;
        resultChanged = true;
      }
      // preview_selected_style_asset is only ever non-null when
      // selected_style differs from 'realistic' (see design.ts) - with
      // today's two-style product decision that means animated_3d.
      if (data.preview_selected_style_asset && data.selected_style === 'animated_3d' && !mergedResult.animated3dUrl) {
        mergedResult.animated3dUrl = `${API_BASE}/api/tripo/asset?design_id=${encodeURIComponent(designId)}&kind=preview_selected_style`;
        resultChanged = true;
      }
      if (resultChanged) patch.generationResult = mergedResult;

      window.PfFunnel?.set(patch);
      log('recoverDesign: restored fields from backend for', designId, patch);
      return patch;
    } catch (err) {
      log('recoverDesign: network error, keeping local state as-is:', err);
      return null;
    }
  }

  // Fase 3 (contrato explícito de selección): explicit, confirmed write
  // path for selected_style - calls PATCH /api/tripo/design and only ever
  // reports success when the backend's own response confirms
  // persisted:true. Never marks the selection as durably saved on a
  // network error, a non-2xx response, or a response missing
  // persisted:true - callers must check the return value and handle a
  // failure visibly (this NEVER throws). Purely additive: does not touch
  // masterTaskId/animated3dTaskId/sideTaskId/base* fields, and does not
  // replace the existing best-effort sync already inside
  // GET /api/tripo/status (kept as a separate safety net).
  async function syncSelectedStyle(selectedStyle) {
    const state = window.PfFunnel ? window.PfFunnel.get() : {};
    const designId = state.designId;
    if (!designId) {
      console.warn(
        '[PRENDFORM] generation-engine.js: syncSelectedStyle() called without a designId - cannot ' +
          'persist the selection to the backend.',
        { selectedStyle }
      );
      window.PfFunnel?.set({ selectedStyleConfirmed: false });
      return { ok: false, persisted: false, reason: 'no_design_id' };
    }

    try {
      const res = await fetchWithTimeout(
        `${API_BASE}/api/tripo/design`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ design_id: designId, selected_style: selectedStyle }),
        },
        10000
      );
      const bodyText = await readBodySafe(res);
      let data = null;
      try {
        data = bodyText ? JSON.parse(bodyText) : null;
      } catch (e) {
        data = null;
      }

      if (res.ok && data && data.persisted === true) {
        window.PfFunnel?.set({ selectedStyleConfirmed: true });
        log('syncSelectedStyle: confirmed by backend:', selectedStyle);
        return { ok: true, persisted: true };
      }

      console.warn(
        '[PRENDFORM] generation-engine.js: syncSelectedStyle() backend did NOT confirm persistence - ' +
          'the selection stays local-only (PfFunnel/UX) for now; a later recoverDesign() call will ' +
          'reconcile once the backend actually has a value.',
        { status: res.status, body: data || bodyText }
      );
      window.PfFunnel?.set({ selectedStyleConfirmed: false });
      return { ok: false, persisted: false, status: res.status };
    } catch (err) {
      console.warn(
        '[PRENDFORM] generation-engine.js: syncSelectedStyle() network error - the selection stays ' +
          'local-only (PfFunnel/UX) for now.',
        err
      );
      window.PfFunnel?.set({ selectedStyleConfirmed: false });
      return { ok: false, persisted: false, error: err };
    }
  }

  function stopPolling() {
    if (pollTimer) clearTimeout(pollTimer);
    pollTimer = null;
  }

  // Whether the "complete" event has already fired for the current polling
  // session (fired once, the moment BOTH style images are ready — see
  // below). Navigation to /pages/elegir-estilo is held back until then, so
  // that page can show its 2 cards fully loaded, with no per-card skeleton
  // wait for the common case.
  let completeFiredForPreview = false;

  // Réaliste IS the master image — ready the instant master_status
  // succeeds, with zero extra Kie.ai call. The customer is unlocked to
  // continue right then; animated_3d (the only other style) keeps
  // generating in the background and is picked up either by this same
  // poll loop (if the customer is still on this page) or by
  // style-picker.js's own independent polling once they're on
  // /pages/elegir-estilo — never blocks getting there in the first place.

  // Time-based visual progress: ticks on its own wall-clock timer (see
  // startProgressTicker), completely independent of the ~2.5s poll cadence,
  // so the bar visibly moves continuously instead of jumping once per poll
  // response (which could look "frozen" between ticks). Eases toward
  // PROGRESS_CAP and never reaches 100 on its own — only true completion
  // (see pollOnce) does that. Covers the WHOLE wait (master + the
  // animated_3d variant), since that's now the full duration the customer
  // sees this screen for.
  const PROGRESS_CAP = 92;
  const PROGRESS_TIME_CONSTANT_S = 30;
  let generationStartedAt = 0;
  let progressTickTimer = null;

  function computeTimeBasedProgress() {
    if (!generationStartedAt) return 7;
    const elapsedS = (Date.now() - generationStartedAt) / 1000;
    const eased = PROGRESS_CAP * (1 - Math.exp(-elapsedS / PROGRESS_TIME_CONSTANT_S));
    return Math.max(7, Math.min(PROGRESS_CAP, eased));
  }

  function startProgressTicker() {
    stopProgressTicker();
    generationStartedAt = Date.now();
    progressTickTimer = setInterval(() => {
      const progress = computeTimeBasedProgress();
      updateGenerationProgress(progress, realMessageForProgress(progress));
      window.PfFunnel?.set({ generationProgress: Math.round(progress) });
    }, 400);
  }

  function stopProgressTicker() {
    if (progressTickTimer) clearInterval(progressTickTimer);
    progressTickTimer = null;
  }

  function startPolling(taskId) {
    // Single controller: stop any previous loop before starting a new one,
    // so there is never more than one active poll timer.
    stopPolling();
    pollFailureCount = 0;
    completeFiredForPreview = false;
    startProgressTicker();
    pollTimer = setTimeout(() => pollOnce(taskId), 500);
  }

  // The variant URL field(s) we're watching for, and where each one lands
  // in generationResult. realistic_url falls back to the legacy
  // figure_image_url name since the realistic master IS the first image
  // this task generates. Two styles only (realistic + animated_3d) —
  // christmas/halloween were removed from the product.
  const VARIANT_FIELD_MAP = {
    realisticUrl: ['realistic_url', 'figure_image_url'],
    animated3dUrl: ['animated_3d_url'],
  };

  function allVariantsReady(result) {
    return !!(result.realisticUrl && result.animated3dUrl);
  }

  /**
   * Current backend contract for this stage: master_task_id/master_status/
   * master_progress/realistic_url describe the realistic MASTER. Once
   * master_status === 'success', the backend generates the animated_3d
   * style variant in the background under the SAME task —
   * animated_3d_url (with its own animated_3d_status, and an overall
   * styles_ready array listing which style keys are already done) shows
   * up progressively in later status responses. The side view (front/side,
   * real fields: side_task_id/side_status/side_view_url, plus
   * front_view_url) is a completely separate, later step that only starts
   * once the customer picks a style on /pages/elegir-estilo — this file
   * never touches it.
   *
   * Legacy fallback: figure_status/figure_progress/figure_image_url are
   * read ONLY if the master_* fields are absent, so an older backend
   * response still works — but master_* is always tried first and never
   * required to also be present alongside the legacy names.
   *
   * "generationStatus: completed" here means "the realistic master (=
   * Réaliste) succeeded" — nothing else is waited on. It fires the instant
   * that happens and unlocks /pages/elegir-estilo right away (see
   * generation-page.js's 'complete' handler); it fires once and is FINAL:
   * nothing that happens afterward while polling for animated_3d (a
   * further success, or a repeated network error) ever changes
   * generationStatus or re-triggers the error UI again. See the catch
   * block below for how a variant-polling failure (e.g. a transient 502)
   * is kept from ever looking like the whole generation failed.
   */
  async function pollOnce(taskId) {
    try {
      // Re-read from PfFunnel fresh on every poll (not a local variable):
      // this is what makes a saved animated_3d_task_id survive a page
      // navigation or a refresh (see style-picker.js's own catch-up poll,
      // which reads the exact same field) — a local variable here would
      // only ever cover this one in-memory polling loop.
      const knownAnimated3dTaskId = (window.PfFunnel && window.PfFunnel.get().animated3dTaskId) || null;
      let url = `${API_BASE}/api/tripo/status?task_id=${encodeURIComponent(taskId)}`;
      if (knownAnimated3dTaskId) {
        url += `&animated_3d_task_id=${encodeURIComponent(knownAnimated3dTaskId)}`;
      }
      const res = await fetchWithTimeout(url, {}, 15000);
      if (!res.ok) {
        const body = await readBodySafe(res);
        console.error('[PRENDFORM] status check failed:', res.status, body);
        throw { code: 'status_failed', status: res.status, body };
      }
      const bodyText = await readBodySafe(res);
      let data;
      try {
        data = JSON.parse(bodyText);
      } catch (e) {
        console.error('[PRENDFORM] status response is not valid JSON:', bodyText);
        throw { code: 'status_invalid_response', body: bodyText };
      }
      pollFailureCount = 0;

      // Persist the backend's animated_3d_task_id (once it starts existing,
      // right after master succeeds) so every poll from here on — this
      // loop, a refresh, or style-picker.js's own polling — reuses THIS
      // SAME task instead of the backend creating a brand new one each
      // time nobody echoes it back (its in-memory memo-cache is only a
      // best-effort safety net, never guaranteed across serverless
      // instances/cold starts).
      if (data.animated_3d_task_id) {
        window.PfFunnel?.set({ animated3dTaskId: data.animated_3d_task_id });
      }

      // master_status is the current contract; figure_status is a
      // non-blocking legacy fallback, never required alongside it.
      const masterStatus = data.master_status || data.figure_status;

      if (masterStatus === 'failed' || masterStatus === 'cancelled') {
        stopPolling();
        handleGenerationError({ message: data.master_error || data.figure_error || 'La génération a échoué.', code: masterStatus });
        return;
      }

      if (masterStatus !== 'success') {
        // The visual progress ticker (startProgressTicker) already advances
        // the bar on its own wall-clock timer — this poll only needs to
        // record the real status, never the percentage. Tighter cadence
        // here (MASTER_POLL_INTERVAL_MS) only shortens how long it takes
        // us to NOTICE master_status flip to 'success' — same read-only
        // status call, no new Kie.ai task.
        log('Master status:', masterStatus || '(none yet)');
        window.PfFunnel?.set({
          generationStatus: masterStatus === 'queued' ? 'queued' : 'processing',
          masterStatus,
        });
        pollTimer = setTimeout(() => pollOnce(taskId), MASTER_POLL_INTERVAL_MS);
        return;
      }

      // masterStatus === 'success': Réaliste (the master image itself) is
      // ready right now, at zero extra cost — unlock navigation immediately
      // instead of waiting on animated_3d too. animated_3d keeps filling in
      // below (and independently again via style-picker.js) whether or not
      // the customer has already moved on.
      const previousResult = (window.PfFunnel && window.PfFunnel.get().generationResult) || {};
      const result = Object.assign({}, previousResult);
      Object.keys(VARIANT_FIELD_MAP).forEach((key) => {
        const backendFields = VARIANT_FIELD_MAP[key];
        const value = backendFields.map((field) => data[field]).find((v) => !!v);
        if (value) result[key] = value;
        else if (!result[key]) result[key] = null;
      });

      // styles_ready is an array of style keys already done (e.g.
      // ["realistic","animated_3d"]), not a boolean.
      const allReadyPerBackend = Array.isArray(data.styles_ready) && data.styles_ready.length >= Object.keys(VARIANT_FIELD_MAP).length;
      const allReady = allReadyPerBackend || allVariantsReady(result);

      window.PfFunnel?.set({
        generationStatus: 'completed',
        generationProgress: 100,
        masterStatus: 'success',
        masterTaskId: taskId,
        generationResult: result,
      });

      stopProgressTicker();

      if (!completeFiredForPreview) {
        completeFiredForPreview = true;
        log('Réaliste (master) ready — navigation unlocked immediately; animated_3d keeps generating in the background.');
        updateGenerationProgress(100, realMessageForProgress(100));
        handleGenerationComplete(result);
      }

      if (allReady) {
        stopPolling();
        return;
      }

      // animated_3d not ready yet — keep polling quietly in the background
      // so it can still fill in, whether the customer is still on this page
      // or has already moved on (style-picker.js polls independently too).
      // This does NOT re-emit 'progress'/'complete' again.
      pollTimer = setTimeout(() => pollOnce(taskId), POLL_INTERVAL_MS);
    } catch (err) {
      pollFailureCount += 1;
      if (pollFailureCount > POLL_RETRY_BACKOFF_MS.length) {
        stopPolling();
        if (completeFiredForPreview) {
          // The master already succeeded and was already handed off as
          // "complete" — a variant-polling network failure (e.g. a 502
          // from the status endpoint) must NEVER retroactively fail the
          // whole generation. Just give up on the missing variants quietly
          // — style-picker.js's own polling can still pick them up later.
          log('Variant polling failed repeatedly — keeping the master result, giving up here:', err);
          return;
        }
        stopProgressTicker();
        handleGenerationError({ message: 'Connexion perdue pendant la génération.', code: 'network' });
        return;
      }
      const delay = POLL_RETRY_BACKOFF_MS[pollFailureCount - 1];
      log('Network hiccup while polling, retrying in', delay, 'ms');
      pollTimer = setTimeout(() => pollOnce(taskId), delay);
    }
  }

  async function startRealGeneration(options) {
    if (generationInFlight) {
      log('startGeneration ignored — a generation is already in flight.');
      return;
    }

    const state = window.PfFunnel ? window.PfFunnel.get() : {};

    // Already completed earlier in this session — reuse the stored result,
    // never regenerate (avoids wasting credits).
    if (state.generationStatus === 'completed' && state.generationResult) {
      log('Generation already completed — reusing stored result.');
      currentJobId = state.generationJobId || null;
      handleGenerationComplete(state.generationResult);
      return;
    }

    // A task is already running (e.g. the customer refreshed the page) —
    // resume polling instead of starting a brand new task.
    if (state.generationJobId && (state.generationStatus === 'queued' || state.generationStatus === 'processing')) {
      log('Resuming existing generation task:', state.generationJobId);
      currentJobId = state.generationJobId;
      generationInFlight = true;
      startPolling(currentJobId);
      return;
    }

    const photoFile = options.photoFile;
    if (!photoFile) {
      handleGenerationError({ message: 'Aucune photo disponible pour lancer la génération.', code: 'no_photo' });
      return;
    }

    // No style requirement here: the backend always generates the
    // realistic master first, before any style is ever chosen. Style is
    // only picked afterward, on /pages/elegir-estilo.
    if (!ACCEPTED_PHOTO_TYPES.includes(photoFile.type)) {
      handleGenerationError({ message: 'Format de photo non pris en charge.', code: 'unsupported_format' });
      return;
    }

    generationInFlight = true;

    try {
      log('Requesting master generation (direct multipart upload to /api/tripo/generate, no style)');
      const generated = await requestGeneration(photoFile, petName);
      currentJobId = generated.task_id;
      log('Master generation started:', currentJobId);

      window.PfFunnel?.set({
        generationJobId: currentJobId,
        masterTaskId: currentJobId,
        generationStatus: 'queued',
        generationProgress: 0,
      });

      log('Starting master polling:', currentJobId);
      startPolling(currentJobId);
    } catch (err) {
      generationInFlight = false;
      console.error('[PRENDFORM] startGeneration failed:', err);
      const code = (err && err.code) || 'unknown';
      handleGenerationError({
        message: GENERATE_ERROR_MESSAGES[code] || 'La génération a échoué au démarrage.',
        code,
      });
    }
  }

  return {
    on,
    startGeneration,
    updateGenerationProgress,
    handleGenerationComplete,
    handleGenerationError,
    retryGeneration,
    recoverDesign,
    syncSelectedStyle,
    clearStaleAttempt,
  };
})();
